/*
 * Portions of this component's visual styling are adapted from shadcn/ui.
 * Copyright (c) 2023 shadcn. Licensed under the MIT License.
 */

import { IconCheck } from "./icons/check.js";
import { IconChevronDown } from "./icons/chevron-down.js";
import { AppButton } from "./app-button.js";
import { AppInput } from "./app-input.js";

interface ComboboxOption { value: string; label: string; disabled?: boolean }

function selectedLabel(options: ComboboxOption[], value: string): string {
  const selected = options.find((option) => option.value === value);
  if (selected !== undefined) return selected.label;
  return "";
}

const styles = css`
  :host { display: block; position: relative; width: 100%; }
  .control { align-items: center; background: transparent; border: 1px solid var(--input); border-radius: var(--radius-md, 8px); box-shadow: 0 1px 2px rgb(0 0 0 / 5%); display: flex; height: 36px; transition: border-color 150ms ease, box-shadow 150ms ease; }
  .control:focus-within { border-color: var(--ring); box-shadow: 0 0 0 3px var(--ring-shadow); }
  app-input { flex: 1; min-width: 0; }
  .trigger { align-self: center; flex: 0 0 auto; margin-right: 3px; }
  .popup { background: var(--popover); border: 1px solid color-mix(in oklab, var(--foreground) 10%, transparent); border-radius: var(--radius-md, 8px); box-shadow: 0 8px 20px rgb(0 0 0 / 12%); box-sizing: border-box; color: var(--popover-foreground); display: none; margin-top: 6px; max-height: 320px; min-width: 100%; overflow-y: auto; padding: 4px; position: absolute; z-index: 50; }
  :host([open]) .popup { display: block; }
  .option { align-items: center; border-radius: var(--radius-sm, 6px); cursor: default; display: flex; font-size: 0.875rem; gap: 8px; min-height: 32px; outline: none; padding: 6px 32px 6px 8px; position: relative; user-select: none; }
  .option:hover, .option:focus-visible { background: var(--accent); color: var(--accent-foreground); }
  .option[aria-disabled="true"] { opacity: 0.5; pointer-events: none; }
  .check { align-items: center; display: none; height: 16px; justify-content: center; position: absolute; right: 8px; width: 16px; }
  .option[aria-selected="true"] .check { display: inline-flex; }
  .empty { color: var(--muted-foreground); font-size: 0.875rem; padding: 8px; text-align: center; }
  :host([disabled]) { opacity: 0.5; pointer-events: none; }
  @media (prefers-color-scheme: dark) { .control { background: color-mix(in oklab, var(--input) 30%, transparent); } }
`;

export class AppCombobox extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppCombobox)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["options","data-options","value","data-value","placeholder","data-placeholder","emptytext","data-empty-text","disabled","data-disabled"];

  #options = [];
  #value = "";
  #placeholder = "Search options";
  #emptyText = "No results found.";
  #disabled = false;
  #query;
  #open;
  #optionsSet = false;
  #valueSet = false;
  #placeholderSet = false;
  #emptyTextSet = false;
  #disabledSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #textStart2;
  #textEnd2;
  #textLast2;
  #textStart3;
  #textEnd3;
  #textLast3;
  #attr0;
  #attr1;
  #attrLast0;
  #attrLast1;
  #attrLast2;
  #attrLast3;
  #hooksLive = false;
  #cleanups = [];
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get options() {
    return this.#options;
  }

  set options(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#options, next)) return;
    this.#options = next;
    this.#optionsSet = true;
    this.#updateBindings('options');
  }

  get value() {
    return this.#value;
  }

  set value(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#value, next)) return;
    this.#value = next;
    this.#valueSet = true;
    this.#updateBindings('value');
  }

  get placeholder() {
    return this.#placeholder;
  }

  set placeholder(value) {
    const raw = value ?? "Search options";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#placeholder, next)) return;
    this.#placeholder = next;
    this.#placeholderSet = true;
    this.#updateBindings('placeholder');
  }

  get emptyText() {
    return this.#emptyText;
  }

  set emptyText(value) {
    const raw = value ?? "No results found.";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#emptyText, next)) return;
    this.#emptyText = next;
    this.#emptyTextSet = true;
    this.#updateBindings('emptyText');
  }

  get disabled() {
    return this.#disabled;
  }

  set disabled(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#disabled, next)) return;
    this.#disabled = next;
    this.#disabledSet = true;
    this.#updateBindings('disabled');
  }

  #setQuery(value) {
    if (Object.is(this.#query, value)) return;
    this.#query = value;
    this.#updateBindings('query');
  }

  #setOpen(value) {
    if (Object.is(this.#open, value)) return;
    this.#open = value;
    this.#updateBindings('open');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-options')) this.options = this.dataset.options;
    if (this.hasAttribute('data-value')) this.value = this.dataset.value;
    if (this.hasAttribute('data-placeholder')) this.placeholder = this.dataset.placeholder;
    if (this.hasAttribute('data-empty-text')) this.emptyText = this.dataset.emptyText;
    if (this.hasAttribute('data-disabled')) this.disabled = this.dataset.disabled;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.options = props.options;
      this.value = props.value;
      this.placeholder = props.placeholder;
      this.emptyText = props.emptyText;
      this.disabled = props.disabled;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    if (!this.#optionsSet) this.options = this.dataset.options;
    if (!this.#valueSet) this.value = this.dataset.value;
    if (!this.#placeholderSet) this.placeholder = this.dataset.placeholder;
    if (!this.#emptyTextSet) this.emptyText = this.dataset.emptyText;
    if (!this.#disabledSet) this.disabled = this.dataset.disabled;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    {
      const cleanup = (() => {
    const close = (event: Event) => {
        if (this.#open && !event.composedPath().includes(this)) {
            this.#setOpen(false);
            this.removeAttribute("open");
        }
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
})();
      if (typeof cleanup === 'function') this.#cleanups.push(cleanup);
    }
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    for (const cleanup of this.#cleanups.splice(0)) cleanup();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'options':
      case 'data-options':
        this.options = newValue;
        break;
      case 'value':
      case 'data-value':
        this.value = newValue;
        break;
      case 'placeholder':
      case 'data-placeholder':
        this.placeholder = newValue;
        break;
      case 'emptytext':
      case 'data-empty-text':
        this.emptyText = newValue;
        break;
      case 'disabled':
      case 'data-disabled':
        this.disabled = newValue;
        break;
    }
  }

  #initState() {
    this.#query = selectedLabel(this.#options, this.#value);
    this.#open = false;
  }

  #render() {
    const visible = this.#options.filter(option => option.label.toLowerCase().includes(this.#query.toLowerCase()));
    const optionItems = visible.map((option) => `<div class="option" role="option" tabindex="0" data-value="${this.#escAttr(option.value)}" aria-selected="${this.#escAttr((option.value === this.#value) ? "true" : "false")}" aria-disabled="${option.disabled ?? false}"><span>${this.#escText(option.label)}</span><span class="check" aria-hidden="true"><icon-check>${""}</icon-check></span></div>`).join("");
    const optionContent = (visible.length === 0) ? `<div class="empty">${this.#escText(this.#emptyText)}</div>` : optionItems;
    return `<style>${this.#escText(styles)}</style><div class="control" role="combobox" aria-label="${this.#escAttr(this.#placeholder)}" aria-expanded="${this.#escAttr(this.#open ? "true" : "false")}" aria-controls="options" data-argon="3906" data-argon-bind="0"><app-input variant="ghost" data-kind="text" data-value="${this.#attrEsc(this.#query)}" data-placeholder="${this.#attrEsc(this.#placeholder)}" data-argon-bind="1"><!--argon-text:0-->${""}<!--/argon-text:0--></app-input><app-button class="trigger" size="icon-xs" variant="ghost" data-argon="4307"><!--argon-text:1-->${""}<!--/argon-text:1--><icon-chevron-down><!--argon-text:2-->${""}<!--/argon-text:2--></icon-chevron-down><span class="sr-only">Toggle options</span></app-button></div><div id="options" class="popup" role="listbox" data-argon="4526"><!--argon-text:3-->${optionContent}<!--/argon-text:3--></div>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
        case 'argon-text:2':
          this.#textStart2 = marker;
          break;
        case '/argon-text:2':
          this.#textEnd2 = marker;
          break;
        case 'argon-text:3':
          this.#textStart3 = marker;
          break;
        case '/argon-text:3':
          this.#textEnd3 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#attr1 = root.querySelector('[data-argon-bind="1"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #attrRaw(v) {
    return typeof v === 'object' ? JSON.stringify(v) : String(v);
  }

  #attrEsc(v) {
    return this.#attrRaw(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
  }

  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const visible = (prop === undefined || prop === 'options' || prop === 'query' || prop === 'emptyText' || prop === 'value') ? (this.#options.filter(option => option.label.toLowerCase().includes(this.#query.toLowerCase()))) : undefined;
    const optionItems = (prop === undefined || prop === 'options' || prop === 'query' || prop === 'emptyText' || prop === 'value') ? (visible.map((option) => `<div class="option" role="option" tabindex="0" data-value="${this.#escAttr(option.value)}" aria-selected="${this.#escAttr((option.value === this.#value) ? "true" : "false")}" aria-disabled="${option.disabled ?? false}"><span>${this.#escText(option.label)}</span><span class="check" aria-hidden="true"><icon-check>${""}</icon-check></span></div>`).join("")) : undefined;
    const optionContent = (prop === undefined || prop === 'options' || prop === 'query' || prop === 'emptyText' || prop === 'value') ? ((visible.length === 0) ? `<div class="empty">${this.#escText(this.#emptyText)}</div>` : optionItems) : undefined;
    if (prop === undefined || prop === 'query' || prop === 'placeholder') {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast2) {
        this.#textLast2 = __argonNext;
        this.#replaceRange(this.#textStart2, this.#textEnd2, __argonNext);
      }
    }
    if (prop === undefined || prop === 'options' || prop === 'query' || prop === 'emptyText' || prop === 'value') {
      const __argonNext = `${optionContent}`;
      if (__argonNext !== this.#textLast3) {
        this.#textLast3 = __argonNext;
        this.#replaceRange(this.#textStart3, this.#textEnd3, __argonNext);
      }
    }
    if ((prop === undefined || prop === 'placeholder') && this.#attr0) {
      const __argonNext = `${this.#placeholder}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('aria-label', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'open') && this.#attr0) {
      const __argonNext = `${this.#open ? "true" : "false"}`;
      if (__argonNext !== this.#attrLast1) {
        this.#attrLast1 = __argonNext;
        this.#attr0.setAttribute('aria-expanded', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'query') && this.#attr1) {
      const __argonNext = `${this.#attrRaw(this.#query)}`;
      if (__argonNext !== this.#attrLast2) {
        this.#attrLast2 = __argonNext;
        this.#attr1.setAttribute('data-value', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'placeholder') && this.#attr1) {
      const __argonNext = `${this.#attrRaw(this.#placeholder)}`;
      if (__argonNext !== this.#attrLast3) {
        this.#attrLast3 = __argonNext;
        this.#attr1.setAttribute('data-placeholder', __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '3906').forEach(el => this.#listen(el, '3906', 'focusin', () => { this.#setOpen(true); this.setAttribute("open", ""); }));
    this.#eventTargets(scope, '3906').forEach(el => this.#listen(el, '3906', 'value-change', (event: CustomEvent) => { event.stopPropagation(); this.#setQuery(event.detail.value); this.#setOpen(true); this.setAttribute("open", ""); this.dispatchEvent(new CustomEvent("search-change", { bubbles: true, composed: true, detail: { value: this.#query } })); }));
    this.#eventTargets(scope, '4307').forEach(el => this.#listen(el, '4307', 'click', () => { if (this.#disabled)
    return; this.#setOpen(!this.#open); this.toggleAttribute("open", this.#open); }));
    this.#eventTargets(scope, '4526').forEach(el => this.#listen(el, '4526', 'click', (event: Event) => { const option = (event.target as Element).closest(".option"); if (!option || option.getAttribute("aria-disabled") === "true")
    return; const next = option.getAttribute("data-value") ?? ""; this.#setQuery(option.textContent?.trim() ?? ""); this.#setOpen(false); this.removeAttribute("open"); this.dispatchEvent(new CustomEvent("value-change", { bubbles: true, composed: true, detail: { value: next } })); }));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
